export const linearAlgebraCourse = {
  id: 'linear-algebra',
  title: 'Linear Algebra',
  description: 'Mathematical foundations of vectors, matrices, linear transformations, and their applications in computer science and engineering.',
  status: 'published',
  duration: '15 weeks',
  level: 'Intermediate',
  credits: 3,
  prerequisites: ['Calculus I', 'Mathematical Foundations'],
  learningObjectives: [
    'Master vector operations and geometric interpretations',
    'Understand matrix algebra and computational techniques',
    'Analyze linear transformations and their properties',
    'Compute eigenvalues, eigenvectors, and diagonalization',
    'Apply linear algebra concepts to real-world problems',
    'Implement numerical algorithms for linear systems'
  ],
  modules: [
    {
      id: 'vectors-spaces',
      title: 'Vectors and Vector Spaces',
      description: 'Introduction to vectors, vector operations, and the mathematical structure of vector spaces.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'vector-basics',
          title: 'Vector Operations and Geometry',
          content: `
# Vector Operations and Geometry

## Learning Objectives
- Understand vector notation and geometric interpretation
- Master vector addition, subtraction, and scalar multiplication
- Compute dot products and cross products
- Analyze vector projections and orthogonality

## Vector Fundamentals

### Vector Notation
A vector in n-dimensional space can be represented as:
- Column vector: v = [v₁, v₂, ..., vₙ]ᵀ
- Component form: v = ⟨v₁, v₂, ..., vₙ⟩
- Geometric representation: directed line segment

### Basic Operations

**Vector Addition:**
If u = [u₁, u₂, u₃]ᵀ and v = [v₁, v₂, v₃]ᵀ, then:
u + v = [u₁ + v₁, u₂ + v₂, u₃ + v₃]ᵀ

**Scalar Multiplication:**
c · v = [c·v₁, c·v₂, c·v₃]ᵀ

## Dot Product
The dot product (inner product) of two vectors:
u · v = u₁v₁ + u₂v₂ + u₃v₃ = ||u|| ||v|| cos(θ)

Where θ is the angle between the vectors.

## Cross Product (3D)
For vectors u and v in 3D space:
u × v = [u₂v₃ - u₃v₂, u₃v₁ - u₁v₃, u₁v₂ - u₂v₁]ᵀ

Properties:
- u × v = -v × u (anti-commutative)
- ||u × v|| = ||u|| ||v|| sin(θ)
- u × v ⊥ u and u × v ⊥ v

## Vector Projections
The projection of vector u onto vector v:
proj_v(u) = (u · v / ||v||²) · v

## Applications
- Computer graphics transformations
- Physics force calculations
- Machine learning feature vectors
- Data analysis and dimensionality
          `,
          interactiveExercises: [
            {
              id: 'vector-operations',
              title: 'Vector Operations Calculator',
              description: 'Implement basic vector operations',
              initialCode: `function vectorAdd(u, v) {
    // Implement vector addition
    // u and v are arrays representing vectors
    return [];
}

function dotProduct(u, v) {
    // Implement dot product calculation
    return 0;
}

function vectorMagnitude(v) {
    // Calculate the magnitude (length) of vector v
    return 0;
}

function vectorProjection(u, v) {
    // Project vector u onto vector v
    return [];
}

// Test your functions
const u = [3, 4, 0];
const v = [1, 2, 2];

console.log("u + v =", vectorAdd(u, v));
console.log("u · v =", dotProduct(u, v));
console.log("||u|| =", vectorMagnitude(u));
console.log("proj_v(u) =", vectorProjection(u, v));`,
              solution: `function vectorAdd(u, v) {
    return u.map((ui, i) => ui + v[i]);
}

function dotProduct(u, v) {
    return u.reduce((sum, ui, i) => sum + ui * v[i], 0);
}

function vectorMagnitude(v) {
    return Math.sqrt(v.reduce((sum, vi) => sum + vi * vi, 0));
}

function vectorProjection(u, v) {
    const dotUV = dotProduct(u, v);
    const dotVV = dotProduct(v, v);
    const scalar = dotUV / dotVV;
    return v.map(vi => scalar * vi);
}`,
              hints: [
                'Vector addition is component-wise',
                'Dot product sums the products of corresponding components',
                'Magnitude uses the Pythagorean theorem in n dimensions',
                'Projection formula: proj_v(u) = (u·v / v·v) * v'
              ]
            }
          ]
        },
        {
          id: 'vector-spaces',
          title: 'Vector Spaces and Subspaces',
          content: `
# Vector Spaces and Subspaces

## Learning Objectives
- Understand the axioms of vector spaces
- Identify subspaces and verify subspace properties
- Work with span, linear independence, and basis
- Compute dimensions of vector spaces

## Vector Space Definition
A vector space V over a field F (usually ℝ or ℂ) satisfies:

### Closure Properties
1. u + v ∈ V for all u, v ∈ V
2. c·v ∈ V for all c ∈ F, v ∈ V

### Associativity and Commutativity
3. (u + v) + w = u + (v + w)
4. u + v = v + u

### Identity Elements
5. ∃ 0 ∈ V such that v + 0 = v for all v ∈ V
6. ∃ 1 ∈ F such that 1·v = v for all v ∈ V

### Inverse Elements
7. For each v ∈ V, ∃ (-v) ∈ V such that v + (-v) = 0

### Distributivity
8. c·(u + v) = c·u + c·v
9. (c + d)·v = c·v + d·v
10. c·(d·v) = (cd)·v

## Subspaces
A subset W ⊆ V is a subspace if:
1. 0 ∈ W (contains zero vector)
2. u, v ∈ W ⟹ u + v ∈ W (closed under addition)
3. v ∈ W, c ∈ F ⟹ c·v ∈ W (closed under scalar multiplication)

## Linear Combinations and Span
- Linear combination: c₁v₁ + c₂v₂ + ... + cₖvₖ
- Span: span{v₁, v₂, ..., vₖ} = all linear combinations of {v₁, v₂, ..., vₖ}

## Linear Independence
Vectors {v₁, v₂, ..., vₖ} are linearly independent if:
c₁v₁ + c₂v₂ + ... + cₖvₖ = 0 ⟹ c₁ = c₂ = ... = cₖ = 0

## Basis and Dimension
- Basis: linearly independent set that spans the space
- Dimension: number of vectors in any basis
- Standard basis for ℝⁿ: {e₁, e₂, ..., eₙ} where eᵢ has 1 in position i, 0 elsewhere

## Examples
### ℝⁿ: n-dimensional real coordinate space
### Polynomials: P_n = {polynomials of degree ≤ n}
### Matrices: M_{m×n} = {m×n matrices}
### Function spaces: C[a,b] = {continuous functions on [a,b]}
          `,
          interactiveExercises: [
            {
              id: 'subspace-verification',
              title: 'Subspace Verification',
              description: 'Verify if given sets are subspaces',
              initialCode: `function isSubspace(vectors, operations) {
    // Check if the given set of vectors forms a subspace
    // vectors: array of vectors in the set
    // operations: object with add and scalarMultiply functions
    
    // Check 1: Contains zero vector
    function containsZero() {
        // Implement zero vector check
        return false;
    }
    
    // Check 2: Closed under addition
    function closedUnderAddition() {
        // Implement closure under addition check
        return false;
    }
    
    // Check 3: Closed under scalar multiplication
    function closedUnderScalarMultiplication() {
        // Implement closure under scalar multiplication check
        return false;
    }
    
    return containsZero() && closedUnderAddition() && closedUnderScalarMultiplication();
}

function checkLinearIndependence(vectors) {
    // Check if vectors are linearly independent
    // Return true if independent, false if dependent
    return false;
}

function findBasis(vectors) {
    // Find a basis from the given set of vectors
    // Return array of linearly independent vectors that span
    return [];
}

// Test cases
const vectors1 = [[1, 0], [0, 1], [1, 1]];
const vectors2 = [[1, 2], [2, 4]];

console.log("Linear independence test:", checkLinearIndependence(vectors1));
console.log("Basis:", findBasis(vectors1));`,
              solution: `function isSubspace(vectors, operations) {
    function containsZero() {
        const zero = new Array(vectors[0].length).fill(0);
        return vectors.some(v => v.every((component, i) => component === zero[i]));
    }
    
    function closedUnderAddition() {
        for (let i = 0; i < vectors.length; i++) {
            for (let j = i + 1; j < vectors.length; j++) {
                const sum = operations.add(vectors[i], vectors[j]);
                if (!vectors.some(v => v.every((comp, k) => comp === sum[k]))) {
                    return false;
                }
            }
        }
        return true;
    }
    
    function closedUnderScalarMultiplication() {
        const scalars = [-1, 0, 1, 2, 0.5];
        for (let vector of vectors) {
            for (let scalar of scalars) {
                const scaled = operations.scalarMultiply(scalar, vector);
                if (!vectors.some(v => v.every((comp, k) => Math.abs(comp - scaled[k]) < 1e-10))) {
                    return false;
                }
            }
        }
        return true;
    }
    
    return containsZero() && closedUnderAddition() && closedUnderScalarMultiplication();
}

function checkLinearIndependence(vectors) {
    if (vectors.length === 0) return true;
    const n = vectors[0].length;
    const m = vectors.length;
    
    // Create augmented matrix [A|0]
    const matrix = vectors.map(v => [...v, 0]);
    
    // Gaussian elimination
    for (let i = 0; i < Math.min(m, n); i++) {
        // Find pivot
        let pivot = i;
        for (let j = i + 1; j < m; j++) {
            if (Math.abs(matrix[j][i]) > Math.abs(matrix[pivot][i])) {
                pivot = j;
            }
        }
        
        if (Math.abs(matrix[pivot][i]) < 1e-10) continue;
        
        // Swap rows
        [matrix[i], matrix[pivot]] = [matrix[pivot], matrix[i]];
        
        // Eliminate
        for (let j = i + 1; j < m; j++) {
            const factor = matrix[j][i] / matrix[i][i];
            for (let k = i; k <= n; k++) {
                matrix[j][k] -= factor * matrix[i][k];
            }
        }
    }
    
    // Count non-zero rows
    let rank = 0;
    for (let i = 0; i < m; i++) {
        if (matrix[i].slice(0, n).some(x => Math.abs(x) > 1e-10)) {
            rank++;
        }
    }
    
    return rank === vectors.length;
}

function findBasis(vectors) {
    if (vectors.length === 0) return [];
    
    const basis = [];
    for (let vector of vectors) {
        const testSet = [...basis, vector];
        if (checkLinearIndependence(testSet)) {
            basis.push(vector);
        }
    }
    return basis;
}`,
              hints: [
                'Zero vector has all components equal to 0',
                'Linear independence means no vector can be written as a combination of others',
                'Use Gaussian elimination to check independence',
                'A basis is a maximal linearly independent set'
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'matrices-systems',
      title: 'Matrices and Linear Systems',
      description: 'Matrix operations, determinants, inverse matrices, and solving linear systems.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'matrix-operations',
          title: 'Matrix Operations and Properties',
          content: `
# Matrix Operations and Properties

## Learning Objectives
- Master matrix arithmetic operations
- Understand matrix properties and special matrices
- Compute matrix inverses and determinants
- Apply matrices to solve linear systems

## Matrix Basics

### Matrix Notation
An m×n matrix A has elements aᵢⱼ where i is the row (1 to m) and j is the column (1 to n):

A = [a₁₁  a₁₂  ...  a₁ₙ]
    [a₂₁  a₂₂  ...  a₂ₙ]
    [⋮    ⋮    ⋱   ⋮  ]
    [aₘ₁  aₘ₂  ...  aₘₙ]

### Matrix Addition and Scalar Multiplication
- Addition: (A + B)ᵢⱼ = aᵢⱼ + bᵢⱼ (same dimensions required)
- Scalar multiplication: (cA)ᵢⱼ = c·aᵢⱼ

### Matrix Multiplication
For A (m×p) and B (p×n), the product AB is m×n with:
(AB)ᵢⱼ = Σₖ₌₁ᵖ aᵢₖbₖⱼ

**Properties:**
- Not commutative: AB ≠ BA (generally)
- Associative: (AB)C = A(BC)
- Distributive: A(B + C) = AB + AC

## Special Matrices

### Identity Matrix
I = [1  0  ...  0]
    [0  1  ...  0]
    [⋮  ⋮  ⋱   ⋮]
    [0  0  ...  1]

Property: AI = IA = A

### Zero Matrix
O = [0  0  ...  0]
    [0  0  ...  0]
    [⋮  ⋮  ⋱   ⋮]
    [0  0  ...  0]

### Transpose
Aᵀ has elements (Aᵀ)ᵢⱼ = aⱼᵢ

Properties:
- (Aᵀ)ᵀ = A
- (A + B)ᵀ = Aᵀ + Bᵀ
- (AB)ᵀ = BᵀAᵀ

### Symmetric Matrix
A = Aᵀ (must be square)

### Diagonal Matrix
aᵢⱼ = 0 for i ≠ j

## Determinants

### 2×2 Determinant
det(A) = |a  b| = ad - bc
         |c  d|

### 3×3 Determinant (Cofactor Expansion)
det(A) = a₁₁(a₂₂a₃₃ - a₂₃a₃₂) - a₁₂(a₂₁a₃₃ - a₂₃a₃₁) + a₁₃(a₂₁a₃₂ - a₂₂a₃₁)

### Properties
- det(AB) = det(A)det(B)
- det(Aᵀ) = det(A)
- det(cA) = cⁿdet(A) for n×n matrix
- det(A⁻¹) = 1/det(A)

## Matrix Inverse
For square matrix A, if det(A) ≠ 0, then A⁻¹ exists such that:
AA⁻¹ = A⁻¹A = I

### 2×2 Inverse
A⁻¹ = 1/det(A) [d   -b]
                [-c   a]

### Gauss-Jordan Method
[A | I] → [I | A⁻¹] through row operations

## Applications
- Computer graphics transformations
- Cryptography and coding theory
- Economic modeling and optimization
- Network analysis and graph theory
          `,
          interactiveExercises: [
            {
              id: 'matrix-calculator',
              title: 'Matrix Calculator',
              description: 'Implement matrix operations and properties',
              initialCode: `class Matrix {
    constructor(data) {
        this.data = data;
        this.rows = data.length;
        this.cols = data[0].length;
    }
    
    static add(A, B) {
        // Implement matrix addition
        return new Matrix([[]]);
    }
    
    static multiply(A, B) {
        // Implement matrix multiplication
        return new Matrix([[]]);
    }
    
    transpose() {
        // Implement matrix transpose
        return new Matrix([[]]);
    }
    
    determinant() {
        // Implement determinant calculation (for square matrices)
        return 0;
    }
    
    inverse() {
        // Implement matrix inverse using Gauss-Jordan elimination
        return new Matrix([[]]);
    }
    
    static identity(n) {
        // Create n×n identity matrix
        return new Matrix([[]]);
    }
    
    print() {
        console.log(this.data);
    }
}

// Test your implementation
const A = new Matrix([[1, 2], [3, 4]]);
const B = new Matrix([[5, 6], [7, 8]]);

console.log("Matrix A:");
A.print();
console.log("Matrix B:");
B.print();
console.log("A + B:");
Matrix.add(A, B).print();
console.log("A * B:");
Matrix.multiply(A, B).print();
console.log("det(A):", A.determinant());`,
              solution: `class Matrix {
    constructor(data) {
        this.data = data;
        this.rows = data.length;
        this.cols = data[0].length;
    }
    
    static add(A, B) {
        if (A.rows !== B.rows || A.cols !== B.cols) {
            throw new Error("Matrices must have same dimensions for addition");
        }
        
        const result = [];
        for (let i = 0; i < A.rows; i++) {
            result[i] = [];
            for (let j = 0; j < A.cols; j++) {
                result[i][j] = A.data[i][j] + B.data[i][j];
            }
        }
        return new Matrix(result);
    }
    
    static multiply(A, B) {
        if (A.cols !== B.rows) {
            throw new Error("Matrix dimensions incompatible for multiplication");
        }
        
        const result = [];
        for (let i = 0; i < A.rows; i++) {
            result[i] = [];
            for (let j = 0; j < B.cols; j++) {
                let sum = 0;
                for (let k = 0; k < A.cols; k++) {
                    sum += A.data[i][k] * B.data[k][j];
                }
                result[i][j] = sum;
            }
        }
        return new Matrix(result);
    }
    
    transpose() {
        const result = [];
        for (let j = 0; j < this.cols; j++) {
            result[j] = [];
            for (let i = 0; i < this.rows; i++) {
                result[j][i] = this.data[i][j];
            }
        }
        return new Matrix(result);
    }
    
    determinant() {
        if (this.rows !== this.cols) {
            throw new Error("Determinant only defined for square matrices");
        }
        
        if (this.rows === 1) return this.data[0][0];
        if (this.rows === 2) {
            return this.data[0][0] * this.data[1][1] - this.data[0][1] * this.data[1][0];
        }
        
        let det = 0;
        for (let j = 0; j < this.cols; j++) {
            const minor = this.getMinor(0, j);
            const cofactor = Math.pow(-1, j) * this.data[0][j] * minor.determinant();
            det += cofactor;
        }
        return det;
    }
    
    getMinor(row, col) {
        const result = [];
        for (let i = 0; i < this.rows; i++) {
            if (i === row) continue;
            const newRow = [];
            for (let j = 0; j < this.cols; j++) {
                if (j === col) continue;
                newRow.push(this.data[i][j]);
            }
            result.push(newRow);
        }
        return new Matrix(result);
    }
    
    inverse() {
        const det = this.determinant();
        if (Math.abs(det) < 1e-10) {
            throw new Error("Matrix is not invertible (determinant = 0)");
        }
        
        if (this.rows === 2) {
            return new Matrix([
                [this.data[1][1] / det, -this.data[0][1] / det],
                [-this.data[1][0] / det, this.data[0][0] / det]
            ]);
        }
        
        // Gauss-Jordan elimination for larger matrices
        const n = this.rows;
        const augmented = [];
        for (let i = 0; i < n; i++) {
            augmented[i] = [...this.data[i], ...Matrix.identity(n).data[i]];
        }
        
        // Forward elimination
        for (let i = 0; i < n; i++) {
            // Find pivot
            let maxRow = i;
            for (let k = i + 1; k < n; k++) {
                if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
                    maxRow = k;
                }
            }
            [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];
            
            // Make diagonal element 1
            const pivot = augmented[i][i];
            for (let j = 0; j < 2 * n; j++) {
                augmented[i][j] /= pivot;
            }
            
            // Eliminate column
            for (let k = 0; k < n; k++) {
                if (k !== i) {
                    const factor = augmented[k][i];
                    for (let j = 0; j < 2 * n; j++) {
                        augmented[k][j] -= factor * augmented[i][j];
                    }
                }
            }
        }
        
        // Extract inverse matrix
        const inverse = [];
        for (let i = 0; i < n; i++) {
            inverse[i] = augmented[i].slice(n);
        }
        return new Matrix(inverse);
    }
    
    static identity(n) {
        const result = [];
        for (let i = 0; i < n; i++) {
            result[i] = [];
            for (let j = 0; j < n; j++) {
                result[i][j] = i === j ? 1 : 0;
            }
        }
        return new Matrix(result);
    }
    
    print() {
        console.log(this.data);
    }
}`,
              hints: [
                'Matrix addition is element-wise',
                'Matrix multiplication: row × column',
                'Transpose swaps rows and columns',
                'Use cofactor expansion for determinants',
                'Gauss-Jordan elimination creates [A|I] → [I|A⁻¹]'
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'linear-transformations',
      title: 'Linear Transformations',
      description: 'Understanding linear transformations, their matrix representations, and geometric interpretations.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'transformation-basics',
          title: 'Linear Transformations and Matrix Representations',
          content: `
# Linear Transformations and Matrix Representations

## Learning Objectives
- Understand the definition and properties of linear transformations
- Find matrix representations of linear transformations
- Analyze geometric effects of transformations
- Compose and invert linear transformations

## Linear Transformation Definition
A function T: V → W is a linear transformation if:
1. T(u + v) = T(u) + T(v) for all u, v ∈ V
2. T(cu) = cT(u) for all c ∈ F, u ∈ V

## Matrix Representation
Every linear transformation T: ℝⁿ → ℝᵐ can be represented by an m×n matrix A:
T(x) = Ax

### Finding the Matrix
If T: ℝⁿ → ℝᵐ, then the matrix A has columns:
A = [T(e₁) | T(e₂) | ... | T(eₙ)]
where {e₁, e₂, ..., eₙ} is the standard basis for ℝⁿ.

## Common Linear Transformations in ℝ²

### Rotation by angle θ
R_θ = [cos θ  -sin θ]
      [sin θ   cos θ]

### Reflection across x-axis
Ref_x = [1   0]
        [0  -1]

### Reflection across y-axis
Ref_y = [-1  0]
        [0   1]

### Reflection across line y = x
Ref_diag = [0  1]
           [1  0]

### Scaling by factors sx, sy
Scale = [sx  0 ]
        [0   sy]

### Shearing (horizontal)
Shear_x = [1  k]
          [0  1]

### Projection onto x-axis
Proj_x = [1  0]
         [0  0]

## Properties of Linear Transformations

### Kernel (Null Space)
ker(T) = {v ∈ V : T(v) = 0}

### Image (Range)
im(T) = {T(v) : v ∈ V}

### Rank-Nullity Theorem
For T: ℝⁿ → ℝᵐ:
dim(ker(T)) + dim(im(T)) = n

## Invertible Transformations
T is invertible if:
- T is one-to-one (injective)
- T is onto (surjective)
- ker(T) = {0}
- Matrix A is invertible (det(A) ≠ 0)

## Composition of Transformations
If S: ℝⁿ → ℝᵖ and T: ℝᵖ → ℝᵐ, then:
(T ∘ S)(x) = T(S(x))

Matrix representation: T ∘ S corresponds to matrix BA where:
- A represents S
- B represents T

**Important:** Matrix multiplication order is reversed!

## Change of Basis
If T: V → V and B, C are bases for V, then:
[T]_C = P⁻¹[T]_BP
where P is the change of basis matrix from B to C.

## Applications
- Computer graphics and animation
- Image processing and filtering
- Data compression and dimensionality reduction
- Robotics and control systems
- Signal processing and Fourier analysis
          `,
          interactiveExercises: [
            {
              id: 'transformation-visualizer',
              title: 'Linear Transformation Visualizer',
              description: 'Create and visualize linear transformations',
              initialCode: `class LinearTransformation {
    constructor(matrix) {
        this.matrix = matrix;
    }
    
    apply(vector) {
        // Apply transformation to a vector
        return [];
    }
    
    static rotation(angle) {
        // Create rotation transformation matrix
        return new LinearTransformation([[]]);
    }
    
    static reflection(axis) {
        // Create reflection transformation
        // axis: 'x', 'y', or 'diagonal'
        return new LinearTransformation([[]]);
    }
    
    static scaling(sx, sy) {
        // Create scaling transformation
        return new LinearTransformation([[]]);
    }
    
    static shear(k, direction) {
        // Create shear transformation
        // direction: 'x' or 'y'
        return new LinearTransformation([[]]);
    }
    
    compose(other) {
        // Compose this transformation with another
        return new LinearTransformation([[]]);
    }
    
    inverse() {
        // Find inverse transformation
        return new LinearTransformation([[]]);
    }
    
    getKernel() {
        // Find the kernel (null space) of the transformation
        return [];
    }
    
    getImage() {
        // Find basis vectors for the image (range)
        return [];
    }
    
    transformShape(points) {
        // Transform a set of points representing a shape
        return points.map(point => this.apply(point));
    }
}

// Test transformations
const rotation90 = LinearTransformation.rotation(Math.PI / 2);
const reflectionX = LinearTransformation.reflection('x');
const scaling = LinearTransformation.scaling(2, 0.5);

// Create a square
const square = [[0, 0], [1, 0], [1, 1], [0, 1]];

console.log("Original square:", square);
console.log("After 90° rotation:", rotation90.transformShape(square));
console.log("After reflection across x-axis:", reflectionX.transformShape(square));
console.log("After scaling (2, 0.5):", scaling.transformShape(square));`,
              solution: `class LinearTransformation {
    constructor(matrix) {
        this.matrix = matrix;
    }
    
    apply(vector) {
        const result = [];
        for (let i = 0; i < this.matrix.length; i++) {
            let sum = 0;
            for (let j = 0; j < vector.length; j++) {
                sum += this.matrix[i][j] * vector[j];
            }
            result.push(sum);
        }
        return result;
    }
    
    static rotation(angle) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        return new LinearTransformation([
            [cos, -sin],
            [sin, cos]
        ]);
    }
    
    static reflection(axis) {
        switch (axis) {
            case 'x':
                return new LinearTransformation([[1, 0], [0, -1]]);
            case 'y':
                return new LinearTransformation([[-1, 0], [0, 1]]);
            case 'diagonal':
                return new LinearTransformation([[0, 1], [1, 0]]);
            default:
                throw new Error("Invalid axis. Use 'x', 'y', or 'diagonal'");
        }
    }
    
    static scaling(sx, sy) {
        return new LinearTransformation([
            [sx, 0],
            [0, sy]
        ]);
    }
    
    static shear(k, direction) {
        if (direction === 'x') {
            return new LinearTransformation([[1, k], [0, 1]]);
        } else if (direction === 'y') {
            return new LinearTransformation([[1, 0], [k, 1]]);
        } else {
            throw new Error("Direction must be 'x' or 'y'");
        }
    }
    
    compose(other) {
        // Matrix multiplication: this.matrix * other.matrix
        const result = [];
        for (let i = 0; i < this.matrix.length; i++) {
            result[i] = [];
            for (let j = 0; j < other.matrix[0].length; j++) {
                let sum = 0;
                for (let k = 0; k < other.matrix.length; k++) {
                    sum += this.matrix[i][k] * other.matrix[k][j];
                }
                result[i][j] = sum;
            }
        }
        return new LinearTransformation(result);
    }
    
    inverse() {
        if (this.matrix.length === 2 && this.matrix[0].length === 2) {
            const [[a, b], [c, d]] = this.matrix;
            const det = a * d - b * c;
            if (Math.abs(det) < 1e-10) {
                throw new Error("Matrix is not invertible");
            }
            return new LinearTransformation([
                [d / det, -b / det],
                [-c / det, a / det]
            ]);
        } else {
            throw new Error("Inverse only implemented for 2x2 matrices");
        }
    }
    
    getKernel() {
        // For 2x2 matrices, solve Ax = 0
        const [[a, b], [c, d]] = this.matrix;
        const det = a * d - b * c;
        
        if (Math.abs(det) < 1e-10) {
            // Non-trivial kernel exists
            if (Math.abs(a) > 1e-10) {
                return [[-b / a, 1]]; // Basis vector for kernel
            } else if (Math.abs(c) > 1e-10) {
                return [[-d / c, 1]];
            } else {
                return [[1, 0], [0, 1]]; // Entire space is kernel
            }
        } else {
            return []; // Only zero vector in kernel
        }
    }
    
    getImage() {
        // Columns of the matrix that are linearly independent
        const col1 = [this.matrix[0][0], this.matrix[1][0]];
        const col2 = [this.matrix[0][1], this.matrix[1][1]];
        
        // Check if columns are linearly independent
        const det = col1[0] * col2[1] - col1[1] * col2[0];
        if (Math.abs(det) > 1e-10) {
            return [col1, col2]; // Both columns form basis for image
        } else if (col1[0] !== 0 || col1[1] !== 0) {
            return [col1]; // Only first column
        } else if (col2[0] !== 0 || col2[1] !== 0) {
            return [col2]; // Only second column
        } else {
            return []; // Zero transformation
        }
    }
    
    transformShape(points) {
        return points.map(point => this.apply(point));
    }
}`,
              hints: [
                'Matrix-vector multiplication: sum of products',
                'Rotation matrix uses cos and sin of angle',
                'Composition is matrix multiplication (order matters!)',
                'Kernel is null space: solve Ax = 0',
                'Image is column space of the matrix'
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'eigenvalues-eigenvectors',
      title: 'Eigenvalues and Eigenvectors',
      description: 'Computing eigenvalues and eigenvectors, diagonalization, and applications.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'eigen-computation',
          title: 'Computing Eigenvalues and Eigenvectors',
          content: `
# Computing Eigenvalues and Eigenvectors

## Learning Objectives
- Understand the geometric meaning of eigenvalues and eigenvectors
- Compute eigenvalues using the characteristic polynomial
- Find eigenvectors and eigenspaces
- Apply eigenvalue decomposition to matrix problems

## Eigenvalue and Eigenvector Definition
For a square matrix A, a scalar λ is an eigenvalue and non-zero vector v is an eigenvector if:
Av = λv

This means v is transformed to a scalar multiple of itself.

## Characteristic Polynomial
Eigenvalues are roots of the characteristic polynomial:
det(A - λI) = 0

### Process
1. Form the matrix (A - λI)
2. Compute the determinant
3. Solve the resulting polynomial equation
4. The roots are the eigenvalues

## Finding Eigenvectors
For each eigenvalue λᵢ:
1. Form the matrix (A - λᵢI)
2. Solve the homogeneous system (A - λᵢI)v = 0
3. The non-trivial solutions form the eigenspace E(λᵢ)

## Eigenspace
The eigenspace E(λ) = ker(A - λI) contains:
- All eigenvectors corresponding to λ
- The zero vector
- All linear combinations of eigenvectors for λ

## Geometric and Algebraic Multiplicity
- **Algebraic multiplicity**: multiplicity of λ as a root of characteristic polynomial
- **Geometric multiplicity**: dimension of eigenspace E(λ)
- Always: geometric ≤ algebraic multiplicity

## Diagonalization
A matrix A is diagonalizable if there exists invertible P such that:
P⁻¹AP = D (diagonal matrix)

### Conditions for Diagonalization
A is diagonalizable if and only if:
- Sum of geometric multiplicities equals n (matrix size)
- For each eigenvalue: geometric = algebraic multiplicity

### Diagonalization Process
1. Find all eigenvalues λ₁, λ₂, ..., λₖ
2. Find bases for each eigenspace
3. Form matrix P with eigenvectors as columns
4. D = diag(λ₁, λ₂, ..., λₙ) with eigenvalues on diagonal

## Power of Matrices
If A = PDP⁻¹, then:
Aⁿ = PDⁿP⁻¹

This makes computing high powers very efficient.

## Special Cases

### Symmetric Matrices
- Always diagonalizable
- Eigenvalues are real
- Eigenvectors for different eigenvalues are orthogonal
- Can be orthogonally diagonalized: A = QDQᵀ where Q is orthogonal

### Positive Definite Matrices
- Symmetric with all positive eigenvalues
- Important in optimization and quadratic forms

## Applications

### Principal Component Analysis (PCA)
- Find directions of maximum variance in data
- Uses eigendecomposition of covariance matrix

### Stability Analysis
- System stability determined by eigenvalues
- Stable if all eigenvalues have negative real parts

### Markov Chains
- Steady-state distributions are eigenvectors
- Convergence rates determined by eigenvalues

### Vibration Analysis
- Natural frequencies correspond to eigenvalues
- Mode shapes correspond to eigenvectors

### Google PageRank
- Web page importance using dominant eigenvector
- Power method for computing largest eigenvalue
          `,
          interactiveExercises: [
            {
              id: 'eigenvalue-calculator',
              title: 'Eigenvalue and Eigenvector Calculator',
              description: 'Implement eigenvalue computation and diagonalization',
              initialCode: `class EigenSolver {
    constructor(matrix) {
        this.matrix = matrix;
        this.n = matrix.length;
    }
    
    characteristicPolynomial() {
        // Return coefficients of characteristic polynomial det(A - λI)
        // For 2x2: return [c2, c1, c0] for c2*λ² + c1*λ + c0
        return [];
    }
    
    findEigenvalues() {
        // Solve characteristic polynomial to find eigenvalues
        return [];
    }
    
    findEigenvector(eigenvalue) {
        // Find eigenvector(s) for given eigenvalue
        // Solve (A - λI)v = 0
        return [];
    }
    
    findAllEigenvectors() {
        // Find all eigenvalues and their corresponding eigenvectors
        const eigenvalues = this.findEigenvalues();
        const result = [];
        
        for (let lambda of eigenvalues) {
            const eigenvectors = this.findEigenvector(lambda);
            result.push({
                eigenvalue: lambda,
                eigenvectors: eigenvectors,
                geometricMultiplicity: eigenvectors.length
            });
        }
        
        return result;
    }
    
    isDiagonalizable() {
        // Check if matrix is diagonalizable
        return false;
    }
    
    diagonalize() {
        // Return {P, D} where P^(-1) * A * P = D
        return { P: null, D: null };
    }
    
    matrixPower(n) {
        // Compute A^n efficiently using diagonalization
        return null;
    }
    
    static powerMethod(matrix, iterations = 100) {
        // Find dominant eigenvalue and eigenvector using power method
        return { eigenvalue: 0, eigenvector: [] };
    }
}

// Test with a 2x2 matrix
const A = [[4, 1], [2, 3]];
const solver = new EigenSolver(A);

console.log("Matrix A:", A);
console.log("Characteristic polynomial coefficients:", solver.characteristicPolynomial());
console.log("Eigenvalues:", solver.findEigenvalues());
console.log("All eigenvectors:", solver.findAllEigenvectors());
console.log("Is diagonalizable:", solver.isDiagonalizable());

// Test power method
console.log("Power method result:", EigenSolver.powerMethod(A));`,
              solution: `class EigenSolver {
    constructor(matrix) {
        this.matrix = matrix;
        this.n = matrix.length;
    }
    
    characteristicPolynomial() {
        if (this.n === 2) {
            const [[a, b], [c, d]] = this.matrix;
            // det(A - λI) = det([[a-λ, b], [c, d-λ]]) = (a-λ)(d-λ) - bc
            // = λ² - (a+d)λ + (ad-bc) = λ² - trace(A)λ + det(A)
            const trace = a + d;
            const det = a * d - b * c;
            return [1, -trace, det]; // coefficients for λ² - trace*λ + det
        } else {
            throw new Error("Characteristic polynomial only implemented for 2x2 matrices");
        }
    }
    
    findEigenvalues() {
        const coeffs = this.characteristicPolynomial();
        if (coeffs.length === 3) {
            // Solve quadratic equation aλ² + bλ + c = 0
            const [a, b, c] = coeffs;
            const discriminant = b * b - 4 * a * c;
            
            if (discriminant >= 0) {
                const sqrt_disc = Math.sqrt(discriminant);
                return [
                    (-b + sqrt_disc) / (2 * a),
                    (-b - sqrt_disc) / (2 * a)
                ];
            } else {
                // Complex eigenvalues
                const real = -b / (2 * a);
                const imag = Math.sqrt(-discriminant) / (2 * a);
                return [
                    { real, imag },
                    { real, imag: -imag }
                ];
            }
        }
        return [];
    }
    
    findEigenvector(eigenvalue) {
        // Solve (A - λI)v = 0
        if (typeof eigenvalue === 'object') {
            throw new Error("Complex eigenvalues not supported");
        }
        
        const lambda = eigenvalue;
        const A_minus_lambdaI = this.matrix.map((row, i) => 
            row.map((val, j) => i === j ? val - lambda : val)
        );
        
        // For 2x2 matrix [[a, b], [c, d]], solve [a, b; c, d] * [x, y] = [0, 0]
        if (this.n === 2) {
            const [[a, b], [c, d]] = A_minus_lambdaI;
            
            // If a ≠ 0, then x = -b*y/a, so eigenvector is [-b/a, 1]
            if (Math.abs(a) > 1e-10) {
                return [[-b / a, 1]];
            }
            // If c ≠ 0, then x = -d*y/c, so eigenvector is [-d/c, 1]
            else if (Math.abs(c) > 1e-10) {
                return [[-d / c, 1]];
            }
            // If both a and c are 0, check if b or d are non-zero
            else if (Math.abs(b) > 1e-10 || Math.abs(d) > 1e-10) {
                return [[1, 0]]; // [1, 0] is an eigenvector
            }
            else {
                // Zero matrix case - any vector is an eigenvector
                return [[1, 0], [0, 1]];
            }
        }
        
        return [];
    }
    
    findAllEigenvectors() {
        const eigenvalues = this.findEigenvalues();
        const result = [];
        
        // Remove duplicates and count multiplicities
        const uniqueEigenvalues = [];
        const multiplicities = [];
        
        for (let lambda of eigenvalues) {
            if (typeof lambda === 'object') continue; // Skip complex eigenvalues
            
            let found = false;
            for (let i = 0; i < uniqueEigenvalues.length; i++) {
                if (Math.abs(uniqueEigenvalues[i] - lambda) < 1e-10) {
                    multiplicities[i]++;
                    found = true;
                    break;
                }
            }
            if (!found) {
                uniqueEigenvalues.push(lambda);
                multiplicities.push(1);
            }
        }
        
        for (let i = 0; i < uniqueEigenvalues.length; i++) {
            const lambda = uniqueEigenvalues[i];
            const eigenvectors = this.findEigenvector(lambda);
            result.push({
                eigenvalue: lambda,
                algebraicMultiplicity: multiplicities[i],
                eigenvectors: eigenvectors,
                geometricMultiplicity: eigenvectors.length
            });
        }
        
        return result;
    }
    
    isDiagonalizable() {
        const eigenData = this.findAllEigenvectors();
        let totalGeometricMultiplicity = 0;
        
        for (let data of eigenData) {
            if (data.geometricMultiplicity !== data.algebraicMultiplicity) {
                return false;
            }
            totalGeometricMultiplicity += data.geometricMultiplicity;
        }
        
        return totalGeometricMultiplicity === this.n;
    }
    
    diagonalize() {
        if (!this.isDiagonalizable()) {
            return { P: null, D: null };
        }
        
        const eigenData = this.findAllEigenvectors();
        const P = [];
        const D = [];
        
        // Build P matrix from eigenvectors and D matrix from eigenvalues
        let colIndex = 0;
        for (let data of eigenData) {
            for (let eigenvector of data.eigenvectors) {
                if (P.length === 0) {
                    P.push([], []);
                }
                P[0][colIndex] = eigenvector[0];
                P[1][colIndex] = eigenvector[1];
                
                if (D.length === 0) {
                    D.push([0, 0], [0, 0]);
                }
                D[colIndex][colIndex] = data.eigenvalue;
                colIndex++;
            }
        }
        
        return { P, D };
    }
    
    matrixPower(n) {
        const diag = this.diagonalize();
        if (!diag.P) {
            throw new Error("Matrix is not diagonalizable");
        }
        
        // Compute D^n
        const Dn = diag.D.map(row => [...row]);
        for (let i = 0; i < this.n; i++) {
            Dn[i][i] = Math.pow(Dn[i][i], n);
        }
        
        // Return P * D^n * P^(-1)
        const P = diag.P;
        const det_P = P[0][0] * P[1][1] - P[0][1] * P[1][0];
        const P_inv = [
            [P[1][1] / det_P, -P[0][1] / det_P],
            [-P[1][0] / det_P, P[0][0] / det_P]
        ];
        
        // P * Dn
        const temp = [
            [P[0][0] * Dn[0][0] + P[0][1] * Dn[1][0], P[0][0] * Dn[0][1] + P[0][1] * Dn[1][1]],
            [P[1][0] * Dn[0][0] + P[1][1] * Dn[1][0], P[1][0] * Dn[0][1] + P[1][1] * Dn[1][1]]
        ];
        
        // temp * P_inv
        return [
            [temp[0][0] * P_inv[0][0] + temp[0][1] * P_inv[1][0], temp[0][0] * P_inv[0][1] + temp[0][1] * P_inv[1][1]],
            [temp[1][0] * P_inv[0][0] + temp[1][1] * P_inv[1][0], temp[1][0] * P_inv[0][1] + temp[1][1] * P_inv[1][1]]
        ];
    }
    
    static powerMethod(matrix, iterations = 100, tolerance = 1e-10) {
        const n = matrix.length;
        let v = new Array(n).fill(0);
        v[0] = 1; // Initial guess
        
        let eigenvalue = 0;
        
        for (let iter = 0; iter < iterations; iter++) {
            // Multiply A * v
            const Av = matrix.map(row => 
                row.reduce((sum, val, j) => sum + val * v[j], 0)
            );
            
            // Find the largest component (approximate eigenvalue)
            const newEigenvalue = Av.reduce((max, val) => Math.max(max, Math.abs(val)), 0);
            
            // Normalize
            v = Av.map(val => val / newEigenvalue);
            
            // Check convergence
            if (Math.abs(newEigenvalue - eigenvalue) < tolerance) {
                return { eigenvalue: newEigenvalue, eigenvector: v, iterations: iter + 1 };
            }
            
            eigenvalue = newEigenvalue;
        }
        
        return { eigenvalue, eigenvector: v, iterations };
    }
}`,
              hints: [
                'Characteristic polynomial: det(A - λI) = 0',
                'For 2x2: det = (a-λ)(d-λ) - bc',
                'Eigenvector: solve (A - λI)v = 0',
                'Diagonalizable when geometric = algebraic multiplicity',
                'Power method: repeatedly multiply by matrix and normalize'
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'applications',
      title: 'Applications of Linear Algebra',
      description: 'Real-world applications including graphics, data analysis, and optimization.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'computer-graphics',
          title: 'Computer Graphics and Transformations',
          content: `
# Computer Graphics and Transformations

## Learning Objectives
- Apply linear algebra to 2D and 3D computer graphics
- Understand homogeneous coordinates and transformation matrices
- Implement rotation, scaling, translation, and projection
- Create composite transformations for animation

## Homogeneous Coordinates

### 2D Homogeneous Coordinates
Point (x, y) → (x, y, 1)
Vector (x, y) → (x, y, 0)

This allows translation to be represented as matrix multiplication:
[x']   [1  0  tx] [x]
[y'] = [0  1  ty] [y]
[1 ]   [0  0  1 ] [1]

### 3D Homogeneous Coordinates
Point (x, y, z) → (x, y, z, 1)
Vector (x, y, z) → (x, y, z, 0)

## 2D Transformations

### Translation
T(tx, ty) = [1  0  tx]
            [0  1  ty]
            [0  0  1 ]

### Rotation about origin
R(θ) = [cos θ  -sin θ  0]
       [sin θ   cos θ  0]
       [0       0      1]

### Scaling
S(sx, sy) = [sx  0   0]
            [0   sy  0]
            [0   0   1]

### Reflection
- Across x-axis: Ref_x = [1  0  0; 0 -1  0; 0  0  1]
- Across y-axis: Ref_y = [-1 0  0; 0  1  0; 0  0  1]
- Across y=x: Ref_diag = [0  1  0; 1  0  0; 0  0  1]

### Shearing
- Horizontal: Shear_x(k) = [1  k  0; 0  1  0; 0  0  1]
- Vertical: Shear_y(k) = [1  0  0; k  1  0; 0  0  1]

## 3D Transformations

### Translation
T(tx, ty, tz) = [1  0  0  tx]
                [0  1  0  ty]
                [0  0  1  tz]
                [0  0  0  1 ]

### Rotation about x-axis
Rx(θ) = [1  0       0      0]
        [0  cos θ  -sin θ  0]
        [0  sin θ   cos θ  0]
        [0  0       0      1]

### Rotation about y-axis
Ry(θ) = [cos θ   0  sin θ  0]
        [0       1  0      0]
        [-sin θ  0  cos θ  0]
        [0       0  0      1]

### Rotation about z-axis
Rz(θ) = [cos θ  -sin θ  0  0]
        [sin θ   cos θ  0  0]
        [0       0      1  0]
        [0       0      0  1]

### Scaling
S(sx, sy, sz) = [sx  0   0   0]
                [0   sy  0   0]
                [0   0   sz  0]
                [0   0   0   1]

## Composite Transformations
Transformations are composed by matrix multiplication.
**Order matters!** For transformation sequence T₁, T₂, T₃:
Final = T₃ × T₂ × T₁

Example: Rotate about point (px, py):
1. Translate to origin: T(-px, -py)
2. Rotate: R(θ)
3. Translate back: T(px, py)
Result: T(px, py) × R(θ) × T(-px, -py)

## Camera and Projection

### View Transformation
Convert from world coordinates to camera coordinates:
- Position camera at origin
- Align camera's view direction with -z axis
- Align camera's up direction with +y axis

### Perspective Projection
Projects 3D points onto 2D screen:
x' = (d × x) / z
y' = (d × y) / z
where d is the distance to projection plane.

Matrix form:
P = [d  0  0  0]
    [0  d  0  0]
    [0  0  1  0]
    [0  0  1  0]

### Orthographic Projection
Parallel projection (no perspective):
- Remove z-coordinate
- Scale to screen coordinates

## Animation and Interpolation

### Linear Interpolation (LERP)
P(t) = (1-t)P₀ + tP₁ for t ∈ [0,1]

### Rotation Interpolation
- Quaternions for smooth rotation interpolation
- Avoids gimbal lock problems with Euler angles

### Keyframe Animation
Define transformations at key times, interpolate between them.

## Applications
- 3D modeling and rendering
- Game engines and animation
- Virtual and augmented reality
- CAD and engineering design
- Medical imaging and visualization
          `,
          interactiveExercises: [
            {
              id: 'graphics-engine',
              title: '2D Graphics Engine',
              description: 'Build a 2D graphics transformation engine',
              initialCode: `class Transform2D {
    constructor() {
        // Initialize as identity matrix
        this.matrix = [
            [1, 0, 0],
            [0, 1, 0],
            [0, 0, 1]
        ];
    }
    
    static translation(tx, ty) {
        // Create translation transformation
        return new Transform2D();
    }
    
    static rotation(angle) {
        // Create rotation transformation
        return new Transform2D();
    }
    
    static scaling(sx, sy) {
        // Create scaling transformation
        return new Transform2D();
    }
    
    static reflection(axis) {
        // Create reflection transformation
        // axis: 'x', 'y', or 'xy' (diagonal)
        return new Transform2D();
    }
    
    static shear(k, direction) {
        // Create shear transformation
        // direction: 'x' or 'y'
        return new Transform2D();
    }
    
    compose(other) {
        // Compose this transformation with another
        // Returns new Transform2D representing this * other
        return new Transform2D();
    }
    
    transformPoint(point) {
        // Transform a point [x, y]
        return [];
    }
    
    transformPoints(points) {
        // Transform array of points
        return points.map(point => this.transformPoint(point));
    }
    
    inverse() {
        // Return inverse transformation
        return new Transform2D();
    }
    
    static rotateAboutPoint(angle, px, py) {
        // Rotate about a specific point
        return new Transform2D();
    }
    
    static scaleAboutPoint(sx, sy, px, py) {
        // Scale about a specific point
        return new Transform2D();
    }
}

// Animation helper
class Animator {
    static lerp(start, end, t) {
        // Linear interpolation between start and end
        return start + (end - start) * t;
    }
    
    static interpolateTransform(transform1, transform2, t) {
        // Interpolate between two transformations
        return new Transform2D();
    }
    
    static animateRotation(startAngle, endAngle, duration, callback) {
        // Animate rotation over time
        const steps = 60; // 60 FPS
        const deltaTime = duration / steps;
        let currentStep = 0;
        
        const animate = () => {
            const t = currentStep / steps;
            const currentAngle = this.lerp(startAngle, endAngle, t);
            const transform = Transform2D.rotation(currentAngle);
            callback(transform);
            
            currentStep++;
            if (currentStep <= steps) {
                setTimeout(animate, deltaTime * 1000 / 60);
            }
        };
        
        animate();
    }
}

// Test the graphics engine
const square = [[0, 0], [100, 0], [100, 100], [0, 100]];

console.log("Original square:", square);

// Test transformations
const rotation = Transform2D.rotation(Math.PI / 4); // 45 degrees
const scaling = Transform2D.scaling(2, 1.5);
const translation = Transform2D.translation(50, 30);

console.log("After rotation:", rotation.transformPoints(square));
console.log("After scaling:", scaling.transformPoints(square));

// Composite transformation
const composite = translation.compose(scaling).compose(rotation);
console.log("After composite transform:", composite.transformPoints(square));

// Rotate about center of square
const center = [50, 50];
const rotateAboutCenter = Transform2D.rotateAboutPoint(Math.PI / 6, center[0], center[1]);
console.log("Rotate about center:", rotateAboutCenter.transformPoints(square));`,
              solution: `class Transform2D {
    constructor(matrix = null) {
        if (matrix) {
            this.matrix = matrix.map(row => [...row]);
        } else {
            // Initialize as identity matrix
            this.matrix = [
                [1, 0, 0],
                [0, 1, 0],
                [0, 0, 1]
            ];
        }
    }
    
    static translation(tx, ty) {
        const transform = new Transform2D();
        transform.matrix = [
            [1, 0, tx],
            [0, 1, ty],
            [0, 0, 1]
        ];
        return transform;
    }
    
    static rotation(angle) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const transform = new Transform2D();
        transform.matrix = [
            [cos, -sin, 0],
            [sin, cos, 0],
            [0, 0, 1]
        ];
        return transform;
    }
    
    static scaling(sx, sy) {
        const transform = new Transform2D();
        transform.matrix = [
            [sx, 0, 0],
            [0, sy, 0],
            [0, 0, 1]
        ];
        return transform;
    }
    
    static reflection(axis) {
        const transform = new Transform2D();
        switch (axis) {
            case 'x':
                transform.matrix = [[1, 0, 0], [0, -1, 0], [0, 0, 1]];
                break;
            case 'y':
                transform.matrix = [[-1, 0, 0], [0, 1, 0], [0, 0, 1]];
                break;
            case 'xy':
                transform.matrix = [[0, 1, 0], [1, 0, 0], [0, 0, 1]];
                break;
            default:
                throw new Error("Invalid axis. Use 'x', 'y', or 'xy'");
        }
        return transform;
    }
    
    static shear(k, direction) {
        const transform = new Transform2D();
        if (direction === 'x') {
            transform.matrix = [[1, k, 0], [0, 1, 0], [0, 0, 1]];
        } else if (direction === 'y') {
            transform.matrix = [[1, 0, 0], [k, 1, 0], [0, 0, 1]];
        } else {
            throw new Error("Direction must be 'x' or 'y'");
        }
        return transform;
    }
    
    compose(other) {
        // Matrix multiplication: this.matrix * other.matrix
        const result = [];
        for (let i = 0; i < 3; i++) {
            result[i] = [];
            for (let j = 0; j < 3; j++) {
                let sum = 0;
                for (let k = 0; k < 3; k++) {
                    sum += this.matrix[i][k] * other.matrix[k][j];
                }
                result[i][j] = sum;
            }
        }
        return new Transform2D(result);
    }
    
    transformPoint(point) {
        const [x, y] = point;
        const homogeneous = [x, y, 1];
        const result = [];
        
        for (let i = 0; i < 3; i++) {
            let sum = 0;
            for (let j = 0; j < 3; j++) {
                sum += this.matrix[i][j] * homogeneous[j];
            }
            result[i] = sum;
        }
        
        // Convert back to 2D coordinates
        return [result[0] / result[2], result[1] / result[2]];
    }
    
    transformPoints(points) {
        return points.map(point => this.transformPoint(point));
    }
    
    inverse() {
        // For 3x3 transformation matrix, compute inverse
        const [[a, b, c], [d, e, f], [g, h, i]] = this.matrix;
        const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
        
        if (Math.abs(det) < 1e-10) {
            throw new Error("Matrix is not invertible");
        }
        
        const invMatrix = [
            [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det],
            [(f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det],
            [(d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det]
        ];
        
        return new Transform2D(invMatrix);
    }
    
    static rotateAboutPoint(angle, px, py) {
        // Translate to origin, rotate, translate back
        const translate1 = Transform2D.translation(-px, -py);
        const rotate = Transform2D.rotation(angle);
        const translate2 = Transform2D.translation(px, py);
        return translate2.compose(rotate).compose(translate1);
    }
    
    static scaleAboutPoint(sx, sy, px, py) {
        // Translate to origin, scale, translate back
        const translate1 = Transform2D.translation(-px, -py);
        const scale = Transform2D.scaling(sx, sy);
        const translate2 = Transform2D.translation(px, py);
        return translate2.compose(scale).compose(translate1);
    }
}

class Animator {
    static lerp(start, end, t) {
        return start + (end - start) * t;
    }
    
    static interpolateTransform(transform1, transform2, t) {
        // Simple linear interpolation of matrix elements
        const result = [];
        for (let i = 0; i < 3; i++) {
            result[i] = [];
            for (let j = 0; j < 3; j++) {
                result[i][j] = this.lerp(
                    transform1.matrix[i][j],
                    transform2.matrix[i][j],
                    t
                );
            }
        }
        return new Transform2D(result);
    }
    
    static animateRotation(startAngle, endAngle, duration, callback) {
        const steps = 60;
        const deltaTime = duration / steps;
        let currentStep = 0;
        
        const animate = () => {
            const t = currentStep / steps;
            const currentAngle = this.lerp(startAngle, endAngle, t);
            const transform = Transform2D.rotation(currentAngle);
            callback(transform);
            
            currentStep++;
            if (currentStep <= steps) {
                setTimeout(animate, deltaTime * 1000 / 60);
            }
        };
        
        animate();
    }
}`,
              hints: [
                'Use homogeneous coordinates: [x, y, 1] for points',
                'Matrix multiplication for composition',
                'Translation matrix has tx, ty in rightmost column',
                'Rotation uses cos and sin of angle',
                'Compose transformations: T₃ × T₂ × T₁'
              ]
            }
          ]
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'midterm-exam',
      title: 'Midterm Examination',
      type: 'exam',
      description: 'Comprehensive exam covering vectors, matrices, and linear transformations',
      questions: [
        {
          id: 'vector-operations-q1',
          type: 'problem-solving',
          question: 'Given vectors u = [3, -2, 1] and v = [1, 4, -2], compute: (a) u + v, (b) 3u - 2v, (c) u · v, (d) ||u||, (e) proj_v(u)',
          points: 20,
          rubric: 'Correct vector arithmetic (4 pts each), proper notation and units'
        },
        {
          id: 'matrix-operations-q1',
          type: 'problem-solving',
          question: 'For matrices A = [[2, 1], [3, -1]] and B = [[1, 2], [-1, 3]], compute: (a) AB, (b) BA, (c) A⁻¹, (d) det(A), (e) Verify that AA⁻¹ = I',
          points: 25,
          rubric: 'Correct matrix multiplication (5 pts each), determinant calculation (5 pts), inverse verification (10 pts)'
        },
        {
          id: 'linear-transformation-q1',
          type: 'problem-solving',
          question: 'Consider the linear transformation T: ℝ² → ℝ² defined by T([x, y]) = [2x + y, x - y]. (a) Find the matrix representation of T, (b) Find T([3, -1]), (c) Find the kernel of T, (d) Is T invertible?',
          points: 20,
          rubric: 'Matrix representation (5 pts), transformation application (5 pts), kernel calculation (5 pts), invertibility analysis (5 pts)'
        },
        {
          id: 'eigenvalue-q1',
          type: 'problem-solving',
          question: 'Find the eigenvalues and eigenvectors of matrix A = [[3, 1], [0, 2]]. Show all work including the characteristic polynomial.',
          points: 25,
          rubric: 'Characteristic polynomial (8 pts), eigenvalue calculation (8 pts), eigenvector calculation (9 pts)'
        },
        {
          id: 'application-q1',
          type: 'problem-solving',
          question: 'A graphics system needs to rotate a point (4, 2) by 90° counterclockwise about the origin, then scale by factor 2 in x and 0.5 in y. Set up the transformation matrices and find the final coordinates.',
          points: 10,
          rubric: 'Correct transformation matrices (6 pts), proper composition and calculation (4 pts)'
        }
      ],
      timeLimit: 120,
      passingScore: 70
    },
    {
      id: 'final-project',
      title: 'Linear Algebra Application Project',
      type: 'project',
      description: 'Comprehensive project applying linear algebra concepts to real-world problems',
      requirements: [
        'Choose application domain (graphics, data analysis, optimization, etc.)',
        'Implement core linear algebra algorithms from scratch',
        'Demonstrate understanding through working code and analysis',
        'Present results with visualizations and mathematical explanations',
        'Compare your implementation with standard library functions'
      ],
      rubric: {
        'Mathematical Understanding': 25,
        'Implementation Quality': 25,
        'Code Documentation': 15,
        'Problem Analysis': 20,
        'Presentation': 15
      },
      timeLimit: null,
      passingScore: 75
    }
  ],
  resources: [
    {
      id: 'textbook-strang',
      title: 'Introduction to Linear Algebra',
      author: 'Gilbert Strang',
      type: 'textbook',
      url: 'https://math.mit.edu/~gs/linearalgebra/',
      description: 'Comprehensive textbook covering all course topics with clear explanations and applications'
    },
    {
      id: 'khan-academy-linear',
      title: 'Khan Academy: Linear Algebra',
      type: 'online-course',
      url: 'https://www.khanacademy.org/math/linear-algebra',
      description: 'Interactive lessons with step-by-step examples and practice problems'
    },
    {
      id: 'numpy-reference',
      title: 'NumPy Linear Algebra Reference',
      type: 'documentation',
      url: 'https://numpy.org/doc/stable/reference/routines.linalg.html',
      description: 'Official documentation for numerical linear algebra operations in Python'
    },
    {
      id: 'linear-algebra-visualizations',
      title: 'Linear Algebra Visualizations',
      type: 'interactive-tool',
      url: 'http://setosa.io/ev/',
      description: 'Interactive visualizations explaining linear algebra concepts geometrically'
    },
    {
      id: 'wolfram-linear-algebra',
      title: 'Wolfram MathWorld: Linear Algebra',
      type: 'reference',
      url: 'https://mathworld.wolfram.com/LinearAlgebra.html',
      description: 'Comprehensive mathematical reference with definitions and theorems'
    }
  ],
  courseFormat: 'hybrid',
  gradingScale: {
    'A': { min: 90, max: 100 },
    'B': { min: 80, max: 89 },
    'C': { min: 70, max: 79 },
    'D': { min: 60, max: 69 },
    'F': { min: 0, max: 59 }
  },
  policies: {
    attendance: 'Regular attendance is required. More than 3 unexcused absences may result in course failure.',
    lateWork: 'Late assignments penalized 10% per day. No submissions accepted after 1 week.',
    academicIntegrity: 'All work must be original. Collaboration allowed on homework but not exams. Cite all sources.',
    makeup: 'Makeup exams only for documented emergencies. Must be arranged within 48 hours.',
    technology: 'Graphing calculators and computer algebra systems allowed for homework. Exams may restrict technology use.',
    communication: 'Check email and course announcements daily. Response within 24-48 hours for questions.',
    accommodations: 'Students with documented disabilities should contact the Office of Disability Services.'
  }
};